/*
 * Copyright (c) 2024 Helvia Technologies PC <contact@helvia.io>
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { INTEGRATION_GRANT_TYPES } from '../../../constants/main';

import { Checkbox, Col, Form, Input, Radio, Row, Select, Switch, Typography } from 'antd';
import OpenIdConfigurationFormItem from '../../../components/UI/OpenIdConfigurationFormItem';
import LabelWithTooltip from '../../../components/UI/LabelWithTooltip';
import { INTEGRATION_TYPE } from '../../../constants/integrations';
import { API_KNOWLEDGE_MANAGER_URL } from '../../../constants/env';

const MS_DYNAMICS_KEYS = [INTEGRATION_TYPE.MICROSOFT_DYNAMICS_CRM, INTEGRATION_TYPE.MICROSOFT_DYNAMICS_KB];

const ALLOWED_FILE_TYPE_OPTIONS = [
  { value: 'pdf', label: 'PDF (.pdf)' },
  { value: 'docx', label: 'Word (.docx)' },
  { value: 'pptx', label: 'PowerPoint (.pptx)' },
  { value: 'html', label: 'HTML (.html)' },
  { value: 'md', label: 'Markdown (.md)' },
  { value: 'txt', label: 'Text (.txt)' }
];

const DEFAULT_AZURE_BLOB_CONTENT_TYPES = ['pdf'];
const DEFAULT_SEGMENTATION_CONFIG = {
  mode: 'standard',
  articleSize: 'large',
  includeImages: false
};
const getSegmentationSettings = (t) => ([
  {
    key: 'segmentationConfig.mode',
    name: 'segmentationConfig.mode',
    title: (
      <LabelWithTooltip
        label={t('integrations.process_mode', 'Process Mode')}
        tooltip={t(
          'integrations.segmentation_mode_tooltip',
          'Standard: rule-based segmentation. Fast and lightweight. AI-Powered: LLM-backed parsing for complex layouts and tables. Takes longer and uses more resources.'
        )}
        align="left"
      />
    ),
    component: (
      <div className="segmentation-controls segmentation-controls--isolated">
        <Form.Item name={['segmentationConfig', 'mode']}>
          <Radio.Group>
            <Radio.Button value="standard">{t('integrations.standard_mode', 'Standard')}</Radio.Button>
            <Radio.Button value="agentic">{t('integrations.agentic_mode', 'AI-Powered')}</Radio.Button>
          </Radio.Group>
        </Form.Item>
      </div>
    )
  },
  {
    key: 'segmentationConfig.articleSize',
    name: 'segmentationConfig.articleSize',
    title: (
      <LabelWithTooltip
        label={t('integrations.max_article_size', 'Max Article Size')}
        tooltip={t(
          'integrations.max_article_size_tooltip',
          'Maximum characters per generated article. Small ~750, Medium ~1,500, Large ~5,000, XLarge ~9,000.'
        )}
        align="left"
      />
    ),
    component: (
      <div className="segmentation-controls segmentation-controls--isolated">
        <Form.Item name={['segmentationConfig', 'articleSize']}>
          <Radio.Group>
            <Radio.Button value="small">Small</Radio.Button>
            <Radio.Button value="medium">Medium</Radio.Button>
            <Radio.Button value="large">Large</Radio.Button>
            <Radio.Button value="xlarge">XLarge</Radio.Button>
          </Radio.Group>
        </Form.Item>
      </div>
    )
  },
  {
    key: 'segmentationConfig.includeImages',
    name: 'segmentationConfig.includeImages',
    title: (
      <Typography.Text>
        {t('integrations.include_images', 'Include Images')}
      </Typography.Text>
    ),
    component: (
      <Row align="middle" gutter={8}>
        <Col>
          <Form.Item name={['segmentationConfig', 'includeImages']} valuePropName="checked" noStyle>
            <Switch/>
          </Form.Item>
        </Col>
        <Col>
          <Typography.Text type="secondary">
            {t('integrations.include_images_pdf_only', 'Extract images from file. Works on PDFs only.')}
          </Typography.Text>
        </Col>
      </Row>
    )
  }
]);

export const useIntegrationFormConfiguration = (integration, formSubmissionErrors, integrationGrantType, form, { driveOptions = [], isFetchingDrives = false } = {}) => {
  const { t } = useTranslation();
  const knowledgeManagerBaseUrl = useMemo(() => {
    return API_KNOWLEDGE_MANAGER_URL?.replace(/\/+$/, '') || '';
  }, []);

  const webhookUrl = useMemo(() => {
    if (!knowledgeManagerBaseUrl) {
      return '';
    }
    return `${knowledgeManagerBaseUrl}/webhooks/azure-blob`;
  }, [knowledgeManagerBaseUrl]);

  const initialValues = useMemo(() => {
    let initialValues = {};

    if (integration) {
      initialValues = {
        enabled: true,
        ...integration
      };
      if (MS_DYNAMICS_KEYS.includes(integration.type)) {
        if (integration.updatedAt && integration.version) {
          initialValues.versionMajor = integration.version.split('.')[0];
          initialValues.versionMinor = integration.version.split('.')[1];
        } else {
          initialValues.versionMajor = 'v9';
        }
      }
      if(integration.type === INTEGRATION_TYPE.AZURE_AI_LLM) {
        initialValues.apiVersion = '2023-05-15';
      }
      if (integration.type === INTEGRATION_TYPE.AZURE_BLOB_STORAGE_KB) {
        initialValues.allowedContentTypes = integration.allowedContentTypes?.length
          ? integration.allowedContentTypes
          : DEFAULT_AZURE_BLOB_CONTENT_TYPES;
        initialValues.segmentationConfig = {
          mode: integration?.segmentationConfig?.mode || DEFAULT_SEGMENTATION_CONFIG.mode,
          articleSize: integration?.segmentationConfig?.articleSize || DEFAULT_SEGMENTATION_CONFIG.articleSize,
          includeImages: integration?.segmentationConfig?.includeImages ?? DEFAULT_SEGMENTATION_CONFIG.includeImages
        };
      }
      if (integration.type === INTEGRATION_TYPE.SHAREPOINT_KB) {
        if (initialValues.syncRbacPermissions == null) {
          initialValues.syncRbacPermissions = true;
        }
        if (initialValues.syncManagedMetadata == null) {
          initialValues.syncManagedMetadata = true;
        }
        initialValues.segmentationConfig = {
          mode: integration?.segmentationConfig?.mode || DEFAULT_SEGMENTATION_CONFIG.mode,
          articleSize: integration?.segmentationConfig?.articleSize || DEFAULT_SEGMENTATION_CONFIG.articleSize,
          includeImages: integration?.segmentationConfig?.includeImages ?? DEFAULT_SEGMENTATION_CONFIG.includeImages
        };
      }
    }

    return initialValues;
  }, [integration]);

  const description = useMemo(() => {
    if (MS_DYNAMICS_KEYS.includes(integration?.type)) {
      return t('Configure to Microsoft Dynamics 365');
    } else if (integration?.type === INTEGRATION_TYPE.AZURE_BLOB_STORAGE_KB) {
      return t('Configure Azure Blob Storage for event-driven KB sync');
    } else if (integration?.type === INTEGRATION_TYPE.SHAREPOINT_KB) {
      return t('Configure SharePoint for KB sync');
    } else if (integration?.type === INTEGRATION_TYPE.OIDC_AUTHENTICATION) {
      return t('Configure to OpenID');
    } else if (integration?.type === INTEGRATION_TYPE.ZENDESK_TICKETING) {
      return t('Configure to Zendesk account');
    }
    return '';
  }, [integration, t]);

  const settings = useMemo(() => {
    let _settings = [];
    if (MS_DYNAMICS_KEYS.includes(integration?.type)) {
      _settings = [
        {
          name: 'baseUrl',
          title: t('Base URL')
        },
        {
          name: 'clientId',
          title: t('Client ID')
        },
        {
          name: 'clientSecret',
          title: t('Client Secret'),
          placeholder: t('Client Secret will not be visible while editing'),
          password: true
        },
        {
          name: 'azureTenantId',
          title: t('Azure Tenant ID')
        },
        {
          name: 'scope',
          title: t('Scope')
        },
        integration.type === INTEGRATION_TYPE.MICROSOFT_DYNAMICS_CRM && {
          name: 'contactLinkTemplate',
          title: t('CRM Contact Page URL')
        },
        {
          title: t('Version'),
          rules: [
            {
              required: true
            }
          ],
          component: (
            <Row gutter={8} align="top" wrap={false}>
              <Col flex="0 0 100px">
                <Form.Item name="versionMajor">
                  <Select
                    size="large"
                    className="fs-base"
                    options={[
                      {
                        label: 'v9',
                        value: 'v9'
                      },
                      {
                        label: 'v8',
                        value: 'v8'
                      }
                    ]}
                    getPopupContainer={triggerNode => triggerNode.parentElement} // Fix drop-menu position issues by rendering it in the parent element
                  />
                </Form.Item>
              </Col>
              <Col flex="auto">
                <Form.Item
                  name="versionMinor"
                  validateTrigger={['onChange', 'onBlur']}
                  rules={[
                    {
                      required: true,
                      type: 'string',
                      whitespace: true,
                      message: t('Please enter a minor version')
                    }
                  ]}>
                  <Input className="w-100 py-2" size="large" placeholder={t('Minor version')} />
                </Form.Item>
              </Col>
            </Row>
          )
        }
      ];
    } else if (integration?.type === INTEGRATION_TYPE.AZURE_BLOB_STORAGE_KB) {
      _settings = [
        {
          name: 'accountName',
          title: t('Account Name'),
          rules: [
            {
              required: true,
              message: t('Please enter the Azure Storage account name')
            }
          ]
        },
        {
          name: 'containerName',
          title: t('Container Name'),
          rules: [
            {
              required: true,
              message: t('Please enter the blob container name')
            }
          ]
        },
        {
          name: 'sasToken',
          title: t('SAS Token'),
          placeholder: t('SAS Token will not be visible while editing'),
          password: true,
          rules: [
            {
              required: !integration?.updatedAt,
              message: t('Please enter a SAS Token')
            }
          ]
        },
        {
          name: 'pathPrefix',
          title: t('Path Prefix')
        },
        {
          title: t('Allowed File Types'),
          component: (
            <Form.Item name="allowedContentTypes">
              <Select
                mode="multiple"
                size="large"
                placeholder={t('All file types (no filter)')}
                options={ALLOWED_FILE_TYPE_OPTIONS}
                getPopupContainer={triggerNode => triggerNode.parentElement}
              />
            </Form.Item>
          )
        },
        ...getSegmentationSettings(t),
        {
          title: t('Webhook URL'),
          component: (
            <Form.Item className="mb-2">
              {
                webhookUrl ? (
                  <Input size="large" value={webhookUrl} readOnly />
                ) : (
                  <Typography.Text type="secondary">
                    {t('Set REACT_APP_API_KNOWLEDGE_MANAGER_URL to display the webhook URL.')}
                  </Typography.Text>
                )
              }
            </Form.Item>
          )
        }
      ];
    } else if (integration?.type === INTEGRATION_TYPE.OIDC_AUTHENTICATION) {
      _settings = [
        {
          name: 'configurationUrl',
          title: t('OpenID Configuration URL'),
          component: <OpenIdConfigurationFormItem formSubmissionErrors={formSubmissionErrors} />,
          rules: [{
            required: true
          }]
        },

        {
          name: 'grantType',
          title: t('Grand Type'),
          select: [
            {
              value: INTEGRATION_GRANT_TYPES.AUTHORIZATION_CODE,
              label: t('Authorization Code')
            },
            {
              value: INTEGRATION_GRANT_TYPES.AUTHORIZATION_CODE_PKCE,
              label: t('Authorization Code (with PKCE)')
            }
          ],
          rules: [
            {
              required: true,
              message: t('Please enter a grant type')
            }
          ]
        },
        {
          name: 'clientId',
          title: t('Client ID'),
          rules: [
            {
              required: true,
              message: t('Please enter a client ID')
            }
          ]
        },
        {
          name: 'clientSecret',
          title: t('Client Secret'),
          placeholder: t('Client Secret will not be visible while editing'),
          password: true
        },
        integrationGrantType === INTEGRATION_GRANT_TYPES.AUTHORIZATION_CODE_PKCE && {
          name: 'challengeMethod',
          title: t('Challenge Method'),
          select: [
            {
              value: 'SHA_256',
              label: 'SHA-256'
            },
            {
              value: 'PLAIN',
              label: 'Plain'
            }
          ],
          rules: [
            {
              required: true,
              message: t('Please enter a challenge method')
            }
          ]
        },
        {
          name: 'additionalScopes',
          title: t('Additional Scopes'),
          extra: t('Space separated list of additional scopes')
        }
      ];
    } else if (integration?.type === INTEGRATION_TYPE.ZENDESK_TICKETING) {
      _settings = [
        {
          name: 'baseUrl',
          title: t('Base URL'),
          rules: [
            {
              required: true,
              message: t('Please enter a url')
            }
          ]
        },
        {
          name: 'userName',
          title: t('Username'),
          rules: [
            {
              required: true,
              message: t('Please enter a username')
            }
          ]
        },
        {
          name: 'token',
          title: t('Token'),
          placeholder: t('Client token will not be visible while editing'),
          password: true,
          rules: [
            {
              required: true,
              message: t('Please enter a token')
            }
          ]
        }
      ];
    } else if (integration?.type === INTEGRATION_TYPE.GENESYS_LIVECHAT) {
      _settings = [
        {
          name: 'clientId',
          title: t('Genesys OAuth app client ID'),
          rules: [
            {
              required: true,
              message: t('Please enter the OAuth app client ID')
            }
          ]
        },
        {
          name: 'clientSecret',
          title: t('Genesys OAuth app client secret'),
          password: true,
          rules: [
            {
              required: true,
              message: t('Please enter the OAuth app client secret')
            }
          ]
        },
        {
          name: 'region',
          title: t('Genesys organization region. Domain only.'),
          placeholder: t('e.g. mypurecloud.ie'),
          rules: [
            {
              required: true,
              message: t('Please enter the Genesys organization region')
            }
          ]
        },
        {
          name: 'openIntegrationId',
          title: t('Genesys Open Messaging integration ID'),
          placeholder: t('Sunshine Secret will not be visible while editing'),
          rules: [
            {
              required: true,
              message: t('Please enter the Sunshine Secret')
            }
          ]
        },
        {
          title: t('Queue IDs'),
          rules: [
            {
              required: false
            }
          ],
          component: (
            <Form.Item name="queueIds">
              <Select
                mode="tags"
                size="large"
                tokenSeparators={[',']}
                placeholder={t('Genesys Queue IDs that are going to be subscribed.')}
                options={
                  integration?.queueIds?.length > 0 ? integration?.queueIds.map((tag)=> {
                    return {
                      value: tag,
                      label: tag
                    };
                  }) : null
                }
                getPopupContainer={triggerNode => triggerNode.parentElement} // Fix drop-menu position issues by rendering it in the parent element
              />
            </Form.Item>
          )
        },
        {
          name: 'webhookKey',
          title: t('Webhook Key'),
          placeholder: t('Please enter the Webhook Key'),
          tooltip: t('Go to Apps & Integrations -> Conversation Integrations and view details to show the Shared secret'),
          rules: [
            {
              required: true,
              message: t('Please enter the Webhook Key')
            }
          ]
        }
      ];
    } else if (integration?.type === INTEGRATION_TYPE.ZENDESK_LIVECHAT) {
      _settings = [
        {
          name: 'baseUrl',
          title: t('Zendesk Base URL'),
          rules: [
            {
              required: true,
              message: t('Please enter the Zendesk Base url (without /sc)')
            }
          ]
        },
        {
          name: 'appId',
          title: t('Sunshine App ID'),
          rules: [
            {
              required: true,
              message: t('Please enter the Sunshine App ID')
            }
          ]
        },
        {
          name: 'keyId',
          title: t('Sunshine Conversations Key ID'),
          rules: [
            {
              required: true,
              message: t('Please enter the Sunshine Conversations Key ID')
            }
          ]
        },
        {
          name: 'secret',
          title: t('Sunshine Secret'),
          placeholder: t('Sunshine Secret will not be visible while editing'),
          password: true,
          rules: [
            {
              required: true,
              message: t('Please enter the Sunshine Secret')
            }
          ]
        },
        {
          name: 'webhookId',
          title: t('Webhook Id'),
          placeholder: t('Please enter the Webhook Id'),
          rules: [
            {
              required: true,
              message: t('Please enter the Webhook Id')
            }
          ]
        },
        {
          name: 'webhookKey',
          title: t('Webhook Key'),
          placeholder: t('Please enter the Webhook Key'),
          tooltip: t('Go to Apps & Integrations -> Conversation Integrations and view details to show the Shared secret'),
          rules: [
            {
              required: true,
              message: t('Please enter the Webhook Key')
            }
          ]
        }
      ];
    } else if (integration?.type === INTEGRATION_TYPE.CISCO_LIVECHAT) {
      _settings = [
        {
          name: 'clientId',
          title: t('Client ID'),
          rules: [
            {
              required: true,
              message: t('Please enter a client ID')
            }
          ]
        },
        {
          name: 'clientSecret',
          title: t('Client Secret'),
          placeholder: t('Client Secret will not be visible while editing'),
          password: true,
          rules: [
            {
              required: true,
              message: t('Please enter a client secret')
            }
          ]
        },
        {
          name: 'ccxQueueTag',
          title: t('CCX Queue Tag'),
          rules: [
            {
              required: true,
              message: t('Please enter a CCX queue tag')
            }
          ]
        },
        {
          name: 'chatId',
          title: t('Chat ID'),
          number: true,
          rules: [
            {
              required: true,
              message: t('Please enter a chat ID')
            }
          ]
        },
        {
          name: 'endpoint',
          title: t('Endpoint'),
          rules: [
            {
              required: true,
              message: t('Please enter an endpoint')
            }
          ]
        }
      ];
    } else if (integration?.type === INTEGRATION_TYPE.OPEN_AI_LLM) {
      _settings = [
        {
          name: 'apiKey',
          title: t('API Key'),
          rules: [
            {
              required: true,
              message: t('Please enter the API Key')
            }
          ]
        },
        {
          name: 'project',
          title: t('Project')
        },
        {
          name: 'openAIOrganization',
          title: t('Organization')
        },
        {
          name: 'customUrl',
          title: t('Custom Base URL'),
          rules: [
            {
              type: 'url',
              message: `${t('This field must be a valid url')}.`
            }]
        }
      ];
    } else if (integration?.type === INTEGRATION_TYPE.AZURE_AI_LLM) {
      _settings = [
        {
          name: 'endpoint',
          title: t('Endpoint'),
          rules: [
            {
              required: true,
              message: t('Please enter an endpoint')
            }
          ]
        },
        {
          name: 'apiKey',
          title: t('API Key'),
          password: true,
          rules: [
            {
              required: true,
              message: t('Please enter the API Key')
            }
          ]
        }
      ];
    } else if (integration?.type === INTEGRATION_TYPE.SHAREPOINT_KB) {
      _settings = [
        {
          name: 'siteUrl',
          title: t('Site URL'),
          placeholder: 'https://contoso.sharepoint.com/sites/TeamSite',
          rules: [
            {
              required: true,
              message: t('Please enter a SharePoint site URL')
            }
          ]
        },
        {
          title: t('Document Libraries'),
          rules: [
            {
              required: true,
              message: t('Please select or enter at least one document library name')
            }
          ],
          component: (
            <Form.Item
              name="driveNames"
              rules={[
                {
                  required: true,
                  message: t('Please select or enter at least one document library name')
                }
              ]}
            >
              <Select
                mode="tags"
                size="large"
                tokenSeparators={[',']}
                placeholder={t('Select or type library names')}
                loading={isFetchingDrives}
                options={driveOptions}
                getPopupContainer={triggerNode => triggerNode.parentElement}
              />
            </Form.Item>
          )
        },
        {
          title: t('Allowed File Types'),
          rules: [{ required: false }],
          component: (
            <Form.Item name="allowedContentTypes">
              <Select
                mode="multiple"
                size="large"
                placeholder={t('All file types (no filter)')}
                options={ALLOWED_FILE_TYPE_OPTIONS}
                getPopupContainer={triggerNode => triggerNode.parentElement}
              />
            </Form.Item>
          )
        },
        {
          rules: [{ required: false }],
          component: (
            <Form.Item name="syncRbacPermissions" valuePropName="checked" initialValue={true}
              extra={t('Fetch sharing links and access grants from SharePoint during sync')}>
              <Checkbox>{t('Sync Permissions (RBAC)')}</Checkbox>
            </Form.Item>
          )
        },
        {
          rules: [{ required: false }],
          component: (
            <Form.Item name="syncManagedMetadata" valuePropName="checked" initialValue={true}
              extra={t('Fetch taxonomy term fields from SharePoint during sync')}>
              <Checkbox>{t('Sync Managed Metadata')}</Checkbox>
            </Form.Item>
          )
        },
        ...getSegmentationSettings(t)
      ];
    } else if (integration?.type === INTEGRATION_TYPE.GEMINI_AI_LLM) {
      _settings = [
        {
          name: 'apiKey',
          title: t('API Key'),
          password: true,
          rules: [
            {
              required: true,
              message: t('Please enter the API Key')
            }
          ]
        }
      ];
    } else if (integration?.type === INTEGRATION_TYPE.AZURE_SPEECH) {
      _settings = [
        {
          name: 'region',
          title: t('Region'),
          rules: [
            {
              required: true,
              message: t('Please enter the Azure region (e.g. westeurope)')
            }
          ]
        },
        {
          name: 'subscriptionKey',
          title: t('Subscription Key'),
          placeholder: t('Subscription Key will not be visible while editing'),
          password: true,
          rules: [
            {
              required: !integration?.updatedAt,
              message: t('Please enter the Subscription Key')
            },
            {
              whitespace: true,
              message: t('Please enter the Subscription Key')
            }
          ]
        }
      ];
    }

    return _settings.filter(Boolean);
  }, [integration, integrationGrantType, t, formSubmissionErrors, webhookUrl, driveOptions, isFetchingDrives]);

  return { settings, initialValues, description };
};
